/** The parts of `HocuspocusProviderWebsocket` recovery uses. */
interface RecoverableSocket {
  readonly disconnect: () => void;
  readonly webSocket: Pick<WebSocket, 'close'> | null;
}

/**
 * Recoveries allowed without a successful authentication in between. A session that is still
 * stale after a fresh cookie (or a refresh that keeps failing) stops here instead of looping.
 */
const MAX_RECOVERIES_WITHOUT_AUTH = 2;

interface CollabSessionRecoveryOptions {
  /** Called once the socket has been stopped for good, to tell the user. */
  readonly onSessionLost: () => void;
  /** Any authenticated API request: the API server refreshes the session cookie on its response. */
  readonly refreshSession: () => Promise<unknown>;
  readonly socket: RecoverableSocket;
}

export interface CollabSessionRecovery {
  /** Call when the provider authenticates, so a later expiry gets a fresh set of attempts. */
  readonly markAuthenticated: () => void;
  /** Refresh the cookie, then drop the socket so the provider reconnects and authenticates again. */
  readonly reconnectWithFreshSession: () => Promise<void>;
  /** Refresh the browser's session cookie. Resolves false when the refresh request failed. */
  readonly refreshSession: () => Promise<boolean>;
  /** Stop the socket from reconnecting and tell the user. */
  readonly stop: () => void;
}

/**
 * Collab never refreshes a WorkOS session itself, because it cannot hand the rotated cookie back
 * to the browser. When it reports the session as stale, the browser refreshes through the API
 * and sends the new cookie. Concurrent callers share one refresh request.
 */
export function createCollabSessionRecovery({
  onSessionLost,
  refreshSession,
  socket,
}: CollabSessionRecoveryOptions): CollabSessionRecovery {
  let inFlightRefresh: Promise<boolean> | null = null;
  let recoveriesWithoutAuth = 0;

  function refreshSessionOnce(): Promise<boolean> {
    inFlightRefresh ??= runRefresh(refreshSession).finally(() => {
      inFlightRefresh = null;
    });
    return inFlightRefresh;
  }

  function stop(): void {
    // disconnect() on the shared socket is the only call that stops the provider's retries.
    socket.disconnect();
    onSessionLost();
  }

  async function reconnectWithFreshSession(): Promise<void> {
    if (recoveriesWithoutAuth >= MAX_RECOVERIES_WITHOUT_AUTH) return stop();
    recoveriesWithoutAuth += 1;

    const refreshed = await refreshSessionOnce();
    if (!refreshed) return stop();

    /**
     * Close the raw socket rather than calling disconnect(), which would stop the provider from
     * reconnecting. Hocuspocus marks a document authenticated before running onAuthenticate, so
     * a new token on the same socket would not be checked; only a new socket authenticates again.
     */
    socket.webSocket?.close();
  }

  function markAuthenticated(): void {
    recoveriesWithoutAuth = 0;
  }

  return {
    markAuthenticated,
    reconnectWithFreshSession,
    refreshSession: refreshSessionOnce,
    stop,
  };
}

async function runRefresh(refreshSession: () => Promise<unknown>): Promise<boolean> {
  try {
    await refreshSession();
    return true;
  } catch {
    return false;
  }
}
